import React, { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

export default function MilestoneSelector({ milestones = [], selected = [], onChange }) {
  const [openGroups, setOpenGroups] = useState(new Set());

  // ✅ 기존 그대로 (절대 수정 안 함)
  const buildWBSTree = (flatList) => {
    const map = {};
    const roots = [];

    flatList.forEach((item) => {
      const node = { ...item, children: [] };
      map[item.wbs_code] = node;

      const parts = item.wbs_code.split(".");
      if (parts.length === 1) {
        if (!roots.find((r) => r.wbs_code === item.wbs_code)) roots.push(node);
      } else {
        const parentCode = parts.slice(0, -1).join(".");
        if (map[parentCode]) {
          map[parentCode].children.push(node);
        } else {
          const parentNode = {
            wbs_code: parentCode,
            wbs_name: parentCode.split(".").slice(-1)[0],
            milestones: [],
            children: [node],
          };
          map[parentCode] = parentNode;

          const parentParts = parentCode.split(".");
          if (parentParts.length === 1) {
            roots.push(parentNode);
          } else {
            const higherCode = parentParts.slice(0, -1).join(".");
            if (map[higherCode]) map[higherCode].children.push(parentNode);
            else {
              const higher = {
                wbs_code: higherCode,
                wbs_name: higherCode.split(".").slice(-1)[0],
                milestones: [],
                children: [parentNode],
              };
              map[higherCode] = higher;
              roots.push(higher);
            }
          }
        }
      }
    });

    return roots;
  };

  const tree = buildWBSTree(milestones);

  const getAllGroupIds = (group) => {
    let ids = (group.milestones || []).map((m) => m.id);
    for (const child of group.children || []) {
      ids = [...ids, ...getAllGroupIds(child)];
    }
    return ids;
  };

  const allIds = milestones.flatMap((g) => g.milestones.map((m) => m.id));
  const allSelected = selected.length === allIds.length;

  const toggleAll = () => onChange(allSelected ? [] : allIds);

  const toggleOne = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter((x) => x !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const toggleGroup = (group) => {
    const groupIds = getAllGroupIds(group);
    const allGroupSelected = groupIds.every((id) => selected.includes(id));

    if (allGroupSelected) {
      onChange(selected.filter((x) => !groupIds.includes(x)));
    } else {
      const merged = Array.from(new Set([...selected, ...groupIds]));
      onChange(merged);
    }
  };

  const toggleOpen = (code) => {
    const next = new Set(openGroups);
    next.has(code) ? next.delete(code) : next.add(code);
    setOpenGroups(next);
  };

  // ✅ 렌더링 (스타일만 개선)
  const renderNode = (node, depth = 0) => {
    const groupIds = getAllGroupIds(node);
    const groupSelected = groupIds.length > 0 && groupIds.every((id) => selected.includes(id));
    const groupOpen = openGroups.has(node.wbs_code);

    return (
      <div key={node.wbs_code} className="ml-[2px]" style={{ marginLeft: depth === 0 ? 0 : 6 }}>
        {/* 그룹 헤더 */}
        <div
          className={`flex items-center justify-between cursor-pointer rounded-md px-2 py-1.5 mt-1 transition 
            ${groupOpen ? "bg-gray-100" : "hover:bg-gray-50"}`
          }
          onClick={() => toggleOpen(node.wbs_code)}
        >
          <div className="flex items-center gap-1.5">
            {node.children.length >= 0 ? (
              groupOpen ? (
                <ChevronDown className="w-3.5 h-3.5 text-gray-600" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5 text-gray-600" />
              )
            ) : (
              <div className="w-3.5" />
            )}

            <input
              type="checkbox"
              checked={groupSelected}
              onClick={(e) => e.stopPropagation()}
              onChange={() => toggleGroup(node)}
              className="accent-blue-600 w-4 h-4"
            />

            <span className="text-sm font-medium text-gray-700">
              {node.wbs_name || node.wbs_code.split(".").slice(-1)[0]}
            </span>
          </div>

          <span className="text-xs text-gray-400">
            {groupIds.filter((id) => selected.includes(id)).length} / {groupIds.length}
          </span>
        </div>

        {/* 하위 마일스톤 + 자식 그룹 */}
        {groupOpen && (
          <div className="ml-4 space-y-[2px] border-l border-gray-100 pl-2">
            {(node.milestones || []).map((ms) => (
              <label
                key={ms.id}
                className="flex items-center gap-2 text-sm text-gray-700 pl-4 hover:bg-gray-50 rounded-md py-[2px]"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(ms.id)}
                  onChange={() => toggleOne(ms.id)}
                  className="accent-blue-600 w-4 h-4"
                />
                <span className="truncate">{ms.name}</span>
              </label>
            ))}

            {node.children.map((child) => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {/* ✅ 전체 선택 */}
      <div className="flex items-center gap-2 border border-gray-200 rounded-md px-3 py-1.5 bg-white ">
        <input
          type="checkbox"
          checked={allSelected}
          onChange={toggleAll}
          className="accent-blue-600 w-4 h-4"
        />
        <span className="font-medium text-gray-800 text-sm">전체 선택</span>
      </div>

      <div className="border-t border-gray-200" />

      {/* ✅ 루트 노드 */}
      {tree.length === 0 ? (
        <p className="text-gray-500 text-sm text-center py-3">표시할 마일스톤이 없습니다.</p>
      ) : (
        tree.map((node) => renderNode(node))
      )}
    </div>
  );
}
